import type { Locale } from "../shared/dto";
import { intlLanguage } from "./date-format";

// Resources as readable cards for the partner: Czech field labels, links as
// links, states in words. Technical bookkeeping (UUID references, provenance
// versions) never renders as a "fact"; the full JSON stays behind the
// admin-only "Technická data" fold, the same pattern as the Activity and
// Build-brief panes (night rounds N10/N11).

// Fields whose values are machine references, not human facts. The partner
// must never read a UUID or an internal version number as content; the admin
// still sees them in the raw fold.
const TECHNICAL_FIELD_RE = /(_id|_ids|_by)$/;
const TECHNICAL_FIELDS = new Set(["invite_count_known_at", "source", "source_rules_version"]);

export function isTechnicalResourceField(key: string): boolean {
  return TECHNICAL_FIELD_RE.test(key) || TECHNICAL_FIELDS.has(key);
}

// snake_case OR single lowercase word: both are enum tokens ("done",
// "transferred" used to slip through an underscore-only regex and render as
// raw English in the partner console).
const ENUM_TOKEN_RE = /^[a-z]+(_[a-z]+)*$/;

export function looksLikeEnumToken(value: string): boolean {
  return ENUM_TOKEN_RE.test(value);
}

// Only these fields hold lifecycle states. Identifier fields must render
// verbatim: value-shape sniffing turned telegram_username "pavel_riha" into
// "pavel riha" via the humanized fallback.
const ENUM_VALUE_FIELDS = new Set(["state", "status", "next_actor"]);

export function isEnumResourceField(key: string): boolean {
  return ENUM_VALUE_FIELDS.has(key);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T/;

function formatDate(value: string, locale: Locale): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat(intlLanguage(locale), { dateStyle: "short", timeStyle: "short" }).format(date) : value;
}

function safeT(t: (key: string) => string): (key: string, fallback: string) => string {
  return (key, fallback) => {
    try { return t(key); } catch { return fallback; }
  };
}

export function resourceCardEntries(value: Record<string, unknown>): Array<[string, unknown]> {
  return Object.entries(value).filter(([key, item]) =>
    item !== null && item !== undefined && typeof item !== "object" && item !== ""
    && !isTechnicalResourceField(key));
}

/**
 * A resource whose whole payload is nested bookkeeping (step_confirmations is
 * `{profile: {confirmed_by: ...}}`) has nothing a partner can read. It used to
 * render as an empty card titled with the raw key - "step confirmations" - in
 * the middle of the know-how workspace (audit P2). Admins keep it: the raw
 * fold is exactly what they open it for.
 */
export function ResourceCards(props: { items: Array<Record<string, unknown>>; role: "partner" | "admin"; t: (key: string) => string; locale: Locale }) {
  const safe = safeT(props.t);
  const items = props.role === "admin"
    ? props.items
    : props.items.filter((row) => resourceCardEntries(
        (row.value && typeof row.value === "object" ? row.value : {}) as Record<string, unknown>
      ).length > 0);
  if (!items.length) return <p className="staff-empty">{props.t("staffConsole.emptyResources")}</p>;
  return (
    <div className="staff-card-list">
      {items.map((row) => {
        const value = (row.value && typeof row.value === "object" ? row.value : {}) as Record<string, unknown>;
        const entries = resourceCardEntries(value);
        return (
          <article className="staff-card" key={String(row.key)}>
            <h3>{safe(`staffConsole.resourceName.${String(row.key)}`, String(row.key).replaceAll("_", " "))}</h3>
            <dl className="staff-definition">
              {entries.map(([key, item]) => (
                <div key={key}>
                  <dt>{safe(`staffConsole.resourceField.${key}`, key.replaceAll("_", " "))}</dt>
                  <dd>{typeof item === "string" && /^https?:\/\//.test(item)
                    ? <a className="quiet-link break-text" href={item} target="_blank" rel="noreferrer">{item}</a>
                    : typeof item === "boolean" ? props.t(item ? "staffConsole.yes" : "staffConsole.no")
                    : typeof item === "string" && ISO_DATE_RE.test(item) ? formatDate(item, props.locale)
                    : typeof item === "string" && isEnumResourceField(key) && looksLikeEnumToken(item)
                      ? safe(`staffConsole.enum.${item}`, item.replaceAll("_", " "))
                      : String(item)}</dd>
                </div>
              ))}
            </dl>
            {props.role === "admin" && (
              <details className="raw-details"><summary>{props.t("staffConsole.rawData")}</summary><pre>{JSON.stringify(value, null, 2)}</pre></details>
            )}
          </article>
        );
      })}
    </div>
  );
}
